const BASE_URL = process.env.REACT_APP_API_BASE_URL || `${window.location.origin}/v1`;

let jwtToken = null;

const handleError = (error) => {
  console.error("API Client Error:", error);

  if (error.response?.status === 401) {
    console.warn("Unauthorized request. Redirecting to login...");
    apiClient.clearToken();
    window.location.href = "/#login"; // Adjust based on your app's routing
  }

  throw error; // Re-throw for further handling
};

const logRequest = (method, url) => {
  // Deliberately does not log `options` — it carries the Authorization
  // bearer token and, for /login, the plaintext password in the request
  // body, which would otherwise ship into the browser console in prod.
  console.debug(`HTTP ${method} Request to: ${BASE_URL}${url}`);
};

const updateLastActivity = () => {
  // Dispatch a custom event to notify UserContext
  window.dispatchEvent(new Event('userActivity'));
};

const apiClient = {
  setToken(token) {
    jwtToken = token;
  },

  clearToken() {
    jwtToken = null;
  },

  generateTimestampId(){
    return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`;
  },

  async request(method, url, data = null, isForm = false, responseType = null, returnResponse = false) {
    updateLastActivity(); // Track activity on each request

    // Derive response type if not explicitly set
    if (!responseType) {
      // Remove query parameters before processing
      const urlWithoutQuery = url.split('?')[0];
      const extension = urlWithoutQuery.split('.').pop().toLowerCase();
      if (["json"].includes(extension)) {
          responseType = "json";
      } else if (["zip", "png", "jpg", "jpeg", "gif", "pdf", "xlsx"].includes(extension)) {
          responseType = "blob";
      } else if (["txt", "yml", "csv", "log", "html", "md"].includes(extension)) {
          responseType = "text";
      } else {
          responseType = "json"; // Default to JSON if unknown
      }
    }

    const headers = {
      ...(jwtToken && { Authorization: `Bearer ${jwtToken}` }),
    };

    if (isForm) {
      headers["Content-Type"] = "application/x-www-form-urlencoded";
    } else if (data !== null && data !== undefined) {
      headers["Content-Type"] = "application/json";
    }

    const options = {
      method,
      headers,
      ...(data && { body: isForm ? new URLSearchParams(data).toString() : JSON.stringify(data) }),
    };

    logRequest(method, url);

    try {
      const response = await fetch(`${BASE_URL}${url}`, options);

      if (!response.ok) {
        if (response.status === 401) {
          handleError({ response });
        }

        // Handle 403 with MFA required
        if (response.status === 403) {
          const errorData = await response.json();
          if (errorData.mfa_required) {
            return errorData; // Return the response with mfa_required
          }
        }

        // Extract error details from response
        const fallback = `${method} request to ${url} failed with status ${response.status}`;
        let errorDetail;
        let errorBody = null;
        try {
          errorBody = await response.json();
          errorDetail = errorBody.detail || errorBody.message || fallback;
        } catch {
          // If response body is not JSON, use generic message
          errorDetail = fallback;
        }

        // A structured `detail` (e.g. the input-file problems a refused run
        // returns) would make a useless "[object Object]" message; use the
        // body's human-readable message then, and keep the whole body.
        const error = new Error(
          typeof errorDetail === "string" ? errorDetail : errorBody?.message || fallback
        );
        error.response = {
          status: response.status,
          statusText: response.statusText,
          data: errorDetail,
          body: errorBody,
        };
        throw error;
      }

      // For 204 No Content responses, return null
      if (response.status === 204) {
        return null;
      }

      // For all other successful responses
      try {
        let parsedResult;
        if (responseType !== "blob") {
          const text = await response.text();
          if (!text) {
            parsedResult = null;
          } else {
            try {
              parsedResult = JSON.parse(text);
            } catch {
              parsedResult = text;
            }
          }
        } else {
          parsedResult = await response.blob();
        }
        if (returnResponse) {
          return { data: parsedResult, response };
        }
        return parsedResult;
      } catch (parseError) {
        console.error("Error parsing response:", parseError);
        throw parseError;
      }
    } catch (error) {
      console.error(`Request Error: ${method} ${url}`, error);
      handleError(error);
      throw error;
    }
  },

  get(url, responseType = "json") {
    return this.request("GET", url, null, false, responseType);
  },

  post(url, data) {
    return this.request("POST", url, data);
  },

  postRaw(url, data, responseType = "json") {
    return this.request("POST", url, data, false, responseType);
  },

  postForm(url, data) {
    return this.request("POST", url, data, true);
  },

  put(url, data) {
    return this.request("PUT", url, data);
  },

  delete(url) {
    return this.request("DELETE", url);
  },

  head(url) {
    return this.request("HEAD", url);
  },

  async getRaw(url) {
    try {
      // return this.request("GET", url, null, false, null);
      const response = await this.request("GET", url, null, false, null);
      return response;
    } catch (error) {
      console.error(`Failed to fetch image: ${url}`, error);
      return null;
    }
  },

  /**
   * Fetches an image from the API and returns an object URL for immediate use.
   */
  async getImage(url) {
    try {
      const blob = await this.get(url, "blob");
      if (!blob) return null;
      
      return URL.createObjectURL(blob); // Create and return object URL
    } catch (error) {
      console.error(`Failed to fetch image: ${url}`, error);
      return null;
    }
  }
}  

export default apiClient;
